import React, { useEffect, useRef } from 'react';
import { T } from '../tokens';
import { useTheme } from '../theme';
import { Ico } from '../icons';
import type { IconName } from '../icons';
import { Btn } from './Btn';
import { Badge } from './Badge';

export interface SheetProps {
  children?: React.ReactNode;
  onClose: () => void;
  title?: string;
}

export function Sheet({ children, onClose, title }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prevFocus?.focus?.();
    };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 80,
      background: T.scrim,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440, background: T.surface,
          borderRadius: '24px 24px 0 0',
          padding: '10px 16px calc(18px + env(safe-area-inset-bottom))',
          boxShadow: '0 -10px 44px rgba(0,0,0,0.22)',
          maxHeight: '82vh', overflowY: 'auto', outline: 'none',
        }}
      >
        <div style={{ width: 38, height: 4, borderRadius: 999, background: T.border, margin: '4px auto 14px' }} />
        {title && <div style={{ fontSize: 16, fontWeight: 800, color: T.fg, marginBottom: 12, padding: '0 2px' }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

export interface BulletItem {
  label: string;
  tag?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
}

export interface ConfirmSheetProps {
  title: string;
  message?: string;
  note?: string;
  bullets?: BulletItem[];
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  icon?: IconName;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmSheet({ title, message, note, bullets, confirmLabel, cancelLabel = 'Behold', tone = 'danger', icon = 'alert', onConfirm, onClose }: ConfirmSheetProps) {
  const t = useTheme();
  const Ic = Ico[icon] || Ico.alert;
  const danger = tone === 'danger';
  return (
    <Sheet onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '2px 6px 0' }}>
        <span style={{
          width: 56, height: 56, borderRadius: 17,
          background: danger ? T.errorBg : T.accentLight,
          color: danger ? T.error : t.accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <Ic size={26} />
        </span>
        <div style={{ fontSize: 19, fontWeight: 800, color: T.fg, letterSpacing: '-0.01em' }}>{title}</div>
        {message && <div style={{ fontSize: 13.5, color: T.fg2, fontWeight: 600, lineHeight: 1.5, marginTop: 8 }}>{message}</div>}
      </div>
      {bullets && bullets.length > 0 && (
        <div style={{ marginTop: 16, borderRadius: 13, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          {bullets.map((b, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '10px 13px',
              borderTop: i ? `1px solid ${T.borderLight}` : 'none',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.label}</span>
              {b.tag && <Badge tone={b.tone || 'neutral'} style={{ flexShrink: 0 }}>{b.tag}</Badge>}
            </div>
          ))}
        </div>
      )}
      {note && (
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, padding: '11px 13px', borderRadius: 12, background: T.infoBg }}>
          <Ico.info size={16} style={{ flexShrink: 0, marginTop: 1, color: T.info }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, color: T.info }}>{note}</span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
        <Btn full size="lg" variant={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Btn>
        <Btn full size="lg" variant="ghost" onClick={onClose}>{cancelLabel}</Btn>
      </div>
    </Sheet>
  );
}

export interface ToastData {
  tone: 'success' | 'error' | 'info';
  msg: string;
}

export interface ToastProps {
  toast?: ToastData | null;
}

export function Toast({ toast }: ToastProps) {
  if (!toast) return null;
  const tones: Record<ToastData['tone'], { bg: string; fg: string; icon: React.ReactNode }> = {
    success: { bg: T.accent, fg: T.onAccent, icon: <Ico.checkCircle size={18} /> },
    error: { bg: T.error, fg: T.onAccent, icon: <Ico.alert size={18} /> },
    info: { bg: T.dark, fg: T.onAccent, icon: <Ico.info size={18} /> },
  };
  const c = tones[toast.tone] || tones.info;
  return (
    <div style={{
      position: 'fixed', left: 14, right: 14,
      bottom: 'calc(18px + env(safe-area-inset-bottom))',
      zIndex: 90, display: 'flex', justifyContent: 'center', pointerEvents: 'none',
    }}>
      <div style={{
        maxWidth: 412, width: '100%',
        display: 'flex', alignItems: 'center', gap: 11,
        padding: '13px 16px', borderRadius: 14,
        background: c.bg, color: c.fg,
        boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
        animation: 'pp-toast 220ms ease',
      }}>
        <span style={{ flexShrink: 0, display: 'flex' }}>{c.icon}</span>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>{toast.msg}</div>
      </div>
    </div>
  );
}
